import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Apple } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const SERVICE_ID = process.env.REACT_APP_APPLE_SERVICE_ID;
// Must exactly match a Return URL registered on the Apple Services ID.
const REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI || (typeof window !== "undefined" ? window.location.origin : "");
let appleScriptPromise = null;

function loadAppleJs() {
  if (window.AppleID?.auth) return Promise.resolve();
  if (appleScriptPromise) return appleScriptPromise;
  appleScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error("Failed to load Apple"));
    document.head.appendChild(s);
  });
  return appleScriptPromise;
}

// "Continue with Apple" for the WEB login (Apple Sign in with Apple JS, popup flow).
export function AppleSignInButton({ redirectTo = "/", referralCode }) {
  const navigate = useNavigate();
  const { loginWithApple } = useAuth();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!SERVICE_ID) return;
    loadAppleJs()
      .then(() => window.AppleID.auth.init({
        clientId: SERVICE_ID, scope: "name email", redirectURI: REDIRECT_URI, usePopup: true,
      }))
      .catch(() => {});
  }, []);

  if (!SERVICE_ID) return null;

  const onClick = async () => {
    setBusy(true);
    try {
      await loadAppleJs();
      const res = await window.AppleID.auth.signIn();
      const idToken = res?.authorization?.id_token;
      if (!idToken) throw new Error("Apple sign-in was cancelled");
      // Apple only returns the name on the FIRST authorization.
      const nm = res?.user?.name;
      const name = nm ? [nm.firstName, nm.lastName].filter(Boolean).join(" ") : undefined;
      await loginWithApple(idToken, name, referralCode);
      navigate(redirectTo);
    } catch (err) {
      if (err?.error !== "popup_closed_by_user") toast.error(err?.message || "Apple sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid="apple-signin-button"
      style={{
        width: 320, maxWidth: "100%", margin: "10px auto 0", height: 44, borderRadius: 999,
        background: "#000", color: "#fff", border: "none", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        fontWeight: 600, fontSize: 15, transition: "opacity .2s",
      }}
    >
      <Apple size={18} fill="#fff" /> {busy ? "Connecting…" : "Continue with Apple"}
    </button>
  );
}
